import { Alert, Button, Snackbar, useMediaQuery } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { QueryFunction, useInfiniteQuery } from 'react-query';
import { Mousewheel, Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import { checkIsUserJoined } from '../../../../common/checkRoleUser';
import { convertArrayOfObject } from '../../../../common/convert';
import { FEED_GROUPS_SUGGESTION } from '../../../../constant/QueryKeyName';
import { SUGGESTED_GROUP_PAGE_SIZE } from '../../../../constant/group';
import {
  FolderStatusEnum,
  GroupSuggestionActionEnum,
} from '../../../../interfaces/training.interface';
import GroupSuggestion, {
  DivWrapper,
  GroupSuggestionSkeleton,
} from './GroupSuggestion';

interface IGroupSuggestionSwiper {
  title?: string;
  fetchFunction: QueryFunction<any, (string | number)[]>;
  action?: GroupSuggestionActionEnum;
  queryKey?: (string | number)[];
}

const GroupSuggestionSwiper: React.FunctionComponent<
  IGroupSuggestionSwiper
> = ({
  title = 'Nhóm đề xuất',
  fetchFunction,
  action = GroupSuggestionActionEnum.JoinGroup,
  queryKey = [FEED_GROUPS_SUGGESTION, SUGGESTED_GROUP_PAGE_SIZE],
}) => {
  const matches = useMediaQuery('(min-width:1024px)');
  const { data, isLoading, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery(queryKey, fetchFunction, {
      refetchOnMount: true,
      keepPreviousData: true,
      getNextPageParam: (_lastPage: any, _allPages: any[]) => {
        return _lastPage?.hasNextPage ? _lastPage?.currentPage + 1 : undefined;
      },
      enabled: queryKey.length > 0,
    });

  const loadingCondition: boolean = isLoading;

  const item = useMemo(() => {
    const cloneData = data as any;
    return cloneData ? convertArrayOfObject(cloneData.pages) : [];
  }, [data]);

  const meta = useMemo(() => {
    return (data?.pages[0] as any)?.totalCount || 0;
  }, [data?.pages]);

  const renderMoreButtonCondition = item.length < meta;

  const [openToast, setOpenToast] = useState<{
    type: 'success' | 'error';
    content: string;
  }>({
    type: 'success',
    content: '',
  });

  const toast = (content: string, type: 'success' | 'error' = 'success') => {
    setOpenToast({ type, content });
  };

  return (
    <>
      {item.length > 0 ? (
        <div className="flex flex-col">
          <p
            className={`text-lg font-bold leading-[160%] tracking-[-0.25px] text-[#242424] ${
              matches ? 'mb-4' : '-mb-3'
            } mx-4`}
          >
            {title}
          </p>
          {matches ? (
            !loadingCondition ? (
              <>
                {item?.map((group: any) => (
                  <GroupSuggestion
                    key={group?.id}
                    groupId={group?.id}
                    orientation="horizontal"
                    name={group.name}
                    numOfMembers={group.numOfMembers}
                    avatar={group.avatar}
                    status={group.status as FolderStatusEnum}
                    toast={toast}
                    action={action}
                    role={group?.currentUserPermission}
                    isJoined={checkIsUserJoined(group?.currentUserPermission)}
                  />
                ))}
                {isFetchingNextPage &&
                  new Array(4)
                    .fill(0)
                    .map((_value, index) => (
                      <GroupSuggestionSkeleton key={index} />
                    ))}
                {renderMoreButtonCondition && (
                  <DivWrapper
                    desktopMode
                    widthClassname=""
                    customClassname="shadow-none !bg-transparent"
                    onClick={() => fetchNextPage()}
                  >
                    <Button className="!rounded-[20px] !border !border-solid !border-[transparent] !bg-[transparent] !text-xs !font-semibold !normal-case !leading-4 !text-Primary/Main">
                      <div className="flex items-center justify-between gap-[12.5px]">
                        <span className="flex items-center text-Primary/Main">
                          <AiOutlinePlus />
                        </span>
                        <span className="text-xs font-semibold text-Primary/Main">
                          Xem thêm
                        </span>
                      </div>
                    </Button>
                  </DivWrapper>
                )}
              </>
            ) : (
              new Array(4)
                .fill(0)
                .map((_value, index) => <GroupSuggestionSkeleton key={index} />)
            )
          ) : (
            <SwiperWrapper
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              showMoreButton={renderMoreButtonCondition}
              displayList={
                !loadingCondition
                  ? item.map((group: any) => (
                      <GroupSuggestion
                        key={group.id}
                        groupId={group.id}
                        orientation="horizontal"
                        name={group.name}
                        numOfMembers={group.numOfMembers}
                        avatar={group.avatar}
                        status={group.status as FolderStatusEnum}
                        toast={toast}
                        action={action}
                        role={group?.currentUserPermission}
                        isJoined={checkIsUserJoined(
                          group?.currentUserPermission
                        )}
                      />
                    ))
                  : new Array(4)
                      .fill(0)
                      .map((_value, index) => (
                        <GroupSuggestionSkeleton key={index} />
                      ))
              }
            />
          )}
        </div>
      ) : null}
      <Snackbar
        autoHideDuration={1500}
        disableWindowBlurListener
        open={!!openToast.content}
        onClose={() => toast('')}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        className="rounded-md bg-black/75 p-4"
        style={{ zIndex: 999999999 }}
      >
        <div className="z-[100]">
          {openToast.type === 'success' ? (
            <p className="text-white">{openToast.content}</p>
          ) : (
            <Alert severity="error">{openToast.content}</Alert>
          )}
        </div>
      </Snackbar>
    </>
  );
};

function SwiperWrapper({
  displayList,
  isFetchingNextPage = false,
  fetchNextPage = () => {},
  showMoreButton = true,
}: {
  displayList?: React.ReactElement[];
  isFetchingNextPage?: boolean;
  fetchNextPage: Function;
  showMoreButton?: boolean;
}) {
  const matches = useMediaQuery('(min-width:640px)');

  return (
    <Swiper
      modules={[Mousewheel, Pagination, Navigation]}
      navigation={true}
      mousewheel={true}
      spaceBetween={0} //because GroupSuggestion css to make shadow is not clipped
      slidesPerView={matches ? 2.5 : 1.5}
      watchSlidesProgress={true}
      className="w-[100vw]"
    >
      {displayList
        ? displayList.map((item, index) => {
            return (
              <SwiperSlide
                className={`not-custom pl-4`}
                key={`slider-profile-${index}`}
              >
                {item}
              </SwiperSlide>
            );
          })
        : null}
      {isFetchingNextPage &&
        new Array(4).fill(0).map((_value, index) => (
          <SwiperSlide
            className="not-custom pl-4"
            key={`slider-profile-${index}`}
          >
            <GroupSuggestionSkeleton />
          </SwiperSlide>
        ))}
      {showMoreButton && !isFetchingNextPage && (
        <SwiperSlide className="not-custom pl-4">
          <DivWrapper
            widthClassname="w-[150px]"
            customClassname="p-[36.5px_19.5px] shadow-none !bg-transparent"
          >
            <Button
              onClick={() => fetchNextPage()}
              className="!rounded-[20px] !border !border-solid !border-[#009FE4] !bg-[#B1E7FF33] !px-[10px] !py-[6px] !text-xs !font-semibold !normal-case !leading-4 !text-Primary/Main"
            >
              <div className="flex items-center justify-between gap-[12.5px]">
                <span className="flex items-center text-Primary/Main">
                  <AiOutlinePlus />
                </span>
                <span className="text-xs font-semibold text-Primary/Main">
                  Xem thêm
                </span>
              </div>
            </Button>
          </DivWrapper>
        </SwiperSlide>
      )}
    </Swiper>
  );
}

export default GroupSuggestionSwiper;
